import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../../../queries/queryKeys';
import { collaborationService } from '../api/collaboration-service';

const errorMessage = (error) => error?.response?.data?.message || error?.message || 'Unable to send this note.';

export function useOptimisticCollaborationNotes(collaborationId) {
  const queryClient = useQueryClient();
  const [outgoing, setOutgoing] = useState([]);

  const transmit = useCallback(async (draft) => {
    setOutgoing((items) => items.map((item) => item.localId === draft.localId
      ? { ...item, status: 'sending', error: null }
      : item));
    try {
      const response = await collaborationService.addNote(collaborationId, {
        text: draft.text,
        files: draft.files,
        clientRequestId: draft.clientRequestId,
      });
      const note = response?.data?.data;
      if (note) {
        queryClient.setQueryData(queryKeys.collaborations.notes(collaborationId), (current) => {
          if (!current?.pages?.length || current.pages.some((page) => page.items.some((item) => item.id === note.id))) return current;
          const [latest, ...older] = current.pages;
          return { ...current, pages: [{ ...latest, items: [...latest.items, note] }, ...older] };
        });
      }
      setOutgoing((items) => items.filter((item) => item.localId !== draft.localId));
      void queryClient.invalidateQueries({ queryKey: queryKeys.collaborations.all });
    } catch (error) {
      setOutgoing((items) => items.map((item) => item.localId === draft.localId
        ? { ...item, status: 'failed', error: errorMessage(error) }
        : item));
    }
  }, [collaborationId, queryClient]);

  const send = useCallback(({ text, files }) => {
    const draft = {
      localId: crypto.randomUUID(),
      clientRequestId: crypto.randomUUID(),
      text,
      files: [...files],
      createdAt: new Date().toISOString(),
      status: 'sending',
      error: null,
    };
    setOutgoing((items) => [...items, draft]);
    void transmit(draft);
  }, [transmit]);

  const retry = useCallback((localId) => {
    const draft = outgoing.find((item) => item.localId === localId && item.status === 'failed');
    if (draft) void transmit(draft);
  }, [outgoing, transmit]);

  return { outgoing, send, retry };
}
